import { PERMISSIONS, type Permission } from "../lib/permissions.ts";

import { NAV_TEXTS } from "./texts.ts";

/**
 * Los dos modos de trabajo (§9.2).
 *
 * No son pestañas de contenido: son dos formas de estar en la aplicación, y
 * cada una tiene su propio armazón.
 *
 * - `chat` es el único layout conversacional: historial y memoria a la
 *   izquierda, la conversación al centro y el redactor abajo.
 * - `work` es un panel de control de los de siempre: sus secciones a la
 *   izquierda, contenido al centro. Ni historial ni redactor.
 *
 * Antes eran tres pestañas --Chat, Work y Mecánicas-- que compartían el
 * armazón del chat, así que las dos últimas heredaban el historial y el
 * selector de memoria, que allí no significan nada. Y las pestañas se
 * escondían al abrir una conversación: la única salida era pulsar
 * "Conversación nueva", que es otra acción y además destruye el turno.
 *
 * El conmutador vive arriba, en el mismo sitio en los dos armazones, y no
 * desaparece nunca --salvo con el modo Chat apagado (`CHAT_MODE_ENABLED`),
 * que hoy lo está--. Lo que escriban las dos pantallas pasa por las MISMAS
 * rutas del catálogo que usa el agente (§6), así que no hay una segunda puerta
 * a la escritura.
 *
 * El modo ES la ruta: la tabla de abajo es lo único que traduce entre las dos
 * formas de nombrarlo.
 */
export const WORKSPACE_MODES = ["chat", "work"] as const;

export type WorkspaceMode = (typeof WORKSPACE_MODES)[number];

/**
 * El modo Chat, APAGADO a propósito (decisión del usuario, 2026-10-06): por
 * ahora se trabaja solo en Work.
 *
 * Apagado no es borrado: el armazón, las rutas y el estado del chat siguen
 * ahí, y encenderlo es poner `true` aquí y nada más. Con él apagado:
 * - el conmutador no se dibuja (`ModeSwitch`), tampoco el compacto de
 *   Digitación; un conmutador con un solo modo no conmuta nada;
 * - la raíz (`/`, también con `?c=`) y `/chat` llevan a Work, conservando la
 *   conversación y el mes de la URL (`router.tsx`);
 * - el menú de comandos de Digitación no ofrece «Ir al modo Chat»;
 * - la burbuja del chat dentro de Work no se dibuja (`DashboardShell`), ni
 *   el hueco que se le reservaba abajo (decisión del usuario, 2026-10-08).
 */
export const CHAT_MODE_ENABLED: boolean = false;

/**
 * Chat y Work, y no hay una tercera palabra.
 *
 * El modo se llama igual en el conmutador, en la URL y en el código: en cuanto
 * la pantalla dijera "Panel" y la ruta `/work`, habría dos nombres para lo
 * mismo y la conversación con el usuario tendría que traducir.
 */
export const MODE_LABELS: Record<WorkspaceMode, string> = {
  chat: NAV_TEXTS.chat,
  work: NAV_TEXTS.work,
};

const MODE_PATHS: Record<WorkspaceMode, "/" | "/work"> = {
  chat: "/",
  work: "/work",
};

export const pathOf = (
  mode: WorkspaceMode,
): (typeof MODE_PATHS)[WorkspaceMode] => MODE_PATHS[mode];

export const modeOf = (pathname: string): WorkspaceMode =>
  pathname.startsWith("/work") ? "work" : "chat";

/**
 * Las secciones del panel, en el orden en que se leen.
 *
 * Solo las que existen de verdad: una entrada de menú que lleva a una pantalla
 * vacía promete algo que no existe. Hoy son Documentos --la maestra, con el
 * Resumen, la lista, el proceso y el acierto--, el consumo, solo para el
 * superadmin, y Alfred, para todo el que entra: cada uno ve ahí lo que le
 * ha pedido, y el admin de Alfred, todo. Digitación no está: se entra con «Ir a digitar», y tiene su
 * propio armazón. Las demás del dominio llegan con sus herramientas en el
 * API.
 */
export const DASHBOARD_SECTIONS = [
  {
    key: "documents",
    label: NAV_TEXTS.documents,
    path: "/work/documentos",
    permission: PERMISSIONS.invoiceReviewsRead,
    costs: false,
  },
  {
    key: "usage",
    label: NAV_TEXTS.usage,
    path: "/work/consumo",
    permission: PERMISSIONS.usageRead,
    /* El consumo es todo costo: además del permiso, el superadmin. */
    costs: true,
  },
  {
    key: "alfred",
    label: NAV_TEXTS.alfred,
    path: "/work/alfred",
    /* Todo el que entra le puede pedir algo a Alfred y seguirlo. */
    permission: PERMISSIONS.access,
    costs: false,
  },
  /*
   * Las 7 pantallas del admin viejo (B2), en el orden de su menú
   * (`.referencia/next-viejo/src/components/layout/sidebar.tsx`): el grupo
   * Participantes (Participantes, Distribuidores), el grupo OKRs (Objetivos,
   * Periodos, SKUs), Tareas y Configuración. Aquí son 7 secciones planas
   * porque este panel no tiene submenús. Por ahora son placeholders: cada
   * una la llena la tanda que migra esa pantalla.
   */
  {
    key: "participants",
    label: NAV_TEXTS.participants,
    path: "/work/participantes",
    permission: PERMISSIONS.access,
    costs: false,
  },
  {
    key: "distributors",
    label: NAV_TEXTS.distributors,
    path: "/work/distribuidores",
    permission: PERMISSIONS.access,
    costs: false,
  },
  {
    key: "objectives",
    label: NAV_TEXTS.objectives,
    path: "/work/objetivos",
    permission: PERMISSIONS.access,
    costs: false,
  },
  {
    key: "periods",
    label: NAV_TEXTS.periods,
    path: "/work/periodos",
    permission: PERMISSIONS.access,
    costs: false,
  },
  {
    key: "skus",
    label: NAV_TEXTS.skus,
    path: "/work/skus",
    permission: PERMISSIONS.access,
    costs: false,
  },
  {
    key: "tasks",
    label: NAV_TEXTS.tasks,
    path: "/work/tareas",
    permission: PERMISSIONS.access,
    costs: false,
  },
  {
    key: "settings",
    label: NAV_TEXTS.settings,
    path: "/work/ajustes",
    /* Como `/settings` en el viejo: hace falta `manage`, no solo `access`. */
    permission: PERMISSIONS.manage,
    costs: false,
  },
] as const;

export type DashboardSection = (typeof DASHBOARD_SECTIONS)[number];

/** Cómo se llama lo que se está mirando, para la izquierda de la cabecera. */
export const dashboardTitleOf = (pathname: string): string =>
  dashboardSectionOf(pathname)?.label ?? NAV_TEXTS.work;

/** La sección en la que se está, si es una del menú. */
export const dashboardSectionOf = (
  pathname: string,
): DashboardSection | undefined =>
  [...DASHBOARD_SECTIONS]
    .sort((a, b) => b.path.length - a.path.length)
    .find((candidate) => pathname.startsWith(candidate.path));

/** Lo que la sesión dice de quien mira: lo que hace falta para abrir algo. */
export interface SectionAccess {
  readonly can: (permission: Permission) => boolean;
  readonly canSeeCosts: boolean;
}

/**
 * Si puede abrir esa sección: su permiso y, si es de costos, ser superadmin.
 * Lo mira el menú para ofrecerla y el armazón para dejar pasar por la URL.
 */
export const canOpenSection = (
  section: DashboardSection,
  access: SectionAccess,
): boolean =>
  access.can(section.permission) && (!section.costs || access.canSeeCosts);

/**
 * Si puede ver lo que hay en esa ruta del panel.
 *
 * Esto es el único guard de permiso que necesita una sección del panel: se
 * evalúa en el render de `DashboardShell` (ya con la sesión cargada por el
 * `beforeLoad` de `_authenticated`), así que una ruta nueva en
 * `DASHBOARD_SECTIONS` queda protegida sin agregarle su propio `beforeLoad`
 * en `router.tsx`. Un segundo guard por ruta sería un segundo mecanismo
 * para lo mismo.
 */
export const canOpenPath = (pathname: string, access: SectionAccess) => {
  const section = dashboardSectionOf(pathname);
  return section === undefined
    ? access.can(PERMISSIONS.access)
    : canOpenSection(section, access);
};
